import { Box, Button, Stack, Tooltip } from '@mui/material';
import { DocumentStatus, DocumentType, getReceiptAlertColorCode } from '@synergy/models';
import { FormattedDate, FormattedMessage, FormattedNumber, useIntl } from 'react-intl';
import { MessageIcon, RatingIcon } from '@synergy/theme';
import {
    PaloAgeBadge,
    PaloColumn,
    PaloListView,
    PaloOverdueBadge,
    PaloRowAction,
    PaloStatusPill,
    usePaloListSort,
} from '@synergy/components-palo';
import { ReactElement, useEffect, useState } from 'react';
import { UrlFilter } from '@synergy/components';
import { getDocumentStatusTranslationId } from '@synergy/utils';
import { useLocation, useNavigate } from 'react-router';
import { useSearchParams } from 'react-router-dom';

import * as InquiryActions from '../inquiry-actions';
import { ContactPresenter } from '../../../common/components/contact-presenter/contact-presenter';
import { ConverterReceiptModal } from '../../convert-receipt/converter-receipt-modal';
import { Inquiry } from '../models/inquiry';
import { OfferTransition } from '../../convert-receipt/models/offer/offerTransition';
import { PaloInquiryOverviewFilter } from './palo-inquiry-overview-filter';
import { PaloNameListCell } from '../../../common/components/palo/palo-name-list-cell';
import { PaloNamedFilterChips } from '../../../common/components/palo/palo-named-filter-chips';
import { Transition } from '../../convert-receipt/models/type-transition';
import { fetchTransitions, resetTransitions } from '../../../common/actions/transitions-activity-actions';
import { inquiryPartners } from '../../../common/utils/locked-partner-notice';
import { transitionInquiryIntoPurchaseOrders } from '../../convert-receipt/transition-actions';
import { useAppSelector } from '../../../store/use-appstate-selector';
import { usePaloColumnFilters } from '../../../common/components/palo/use-palo-column-filters';
import { usePaloListViews } from '../../../common/components/palo/use-palo-list-views';
import { useThunkDispatch } from '../../../store/use-thunk-dispatch';

const columns: PaloColumn<Inquiry>[] = [
    {
        key: 'number',
        type: 'documentNumber',
        sortKey: 'number',
        head: <FormattedMessage id="app.table.column.number" />,
        cell: item => (
            <Stack direction="row" alignItems="center" gap={1}>
                <Box>{item.number}</Box>
                {item.receiptAlert && (
                    <Tooltip
                        placement="top-start"
                        title={<Box sx={{ textAlign: 'center' }}>{item.receiptAlert.text}</Box>}
                    >
                        <Stack direction="row" alignItems="center">
                            <MessageIcon
                                color={getReceiptAlertColorCode(item.receiptAlert.type)}
                                sx={{ fontSize: 18 }}
                            />
                        </Stack>
                    </Tooltip>
                )}
            </Stack>
        ),
    },
    {
        key: 'status',
        type: 'status',
        sortKey: 'status',
        head: <FormattedMessage id="app.table.column.status" />,
        // The status is chosen in the navigation on the left and nowhere else: the
        // same filter in two places wrote two different keys into the address and
        // answered each other (LST-S9, GF 13.10.2026). The active one stands as a
        // chip over the list, where it can be taken off again.
        cell: item => (
            <PaloStatusPill
                status={item.status}
                label={<FormattedMessage id={getDocumentStatusTranslationId(item.status)} />}
            />
        ),
    },
    {
        // Beside the status, not inside it: the pill says what the document
        // is, the deadline when it is due. Together in one cell they made the
        // status column 242 px wide and broke LST-S5 (GF 12.10.2026).
        key: 'deadline',
        type: 'deadline',
        head: <FormattedMessage id="app.table.column.deadline" />,
        // A draft not sent yet has no one waiting on its deadline (#2260).
        cell: item => (
            <PaloOverdueBadge due={item.endDate} open={DocumentStatus.Ongoing === item.status} short={true} />
        ),
    },
    {
        // Offers handed in of the suppliers asked, each supplier once (#2260).
        key: 'offers',
        type: 'count',
        head: <FormattedMessage id="app.table.column.offers" />,
        cell: item =>
            item.offerCount ? (
                <Tooltip
                    placement="top"
                    title={
                        <FormattedMessage
                            id="app.inquiries.offerCount.tooltip"
                            values={{
                                submitted: item.offerCount.submitted,
                                declined: item.offerCount.declined,
                                open: item.offerCount.asked - item.offerCount.submitted - item.offerCount.declined,
                            }}
                        />
                    }
                >
                    <Box component="span">
                        {item.offerCount.submitted}/{item.offerCount.asked}
                    </Box>
                </Tooltip>
            ) : (
                '–'
            ),
    },
    {
        key: 'title',
        type: 'name',
        sortKey: 'title',
        head: <FormattedMessage id="app.table.column.title" />,
        cell: item => item.title,
    },
    {
        key: 'customer',
        type: 'name',
        sortKey: 'accountPurchase',
        head: <FormattedMessage id="app.table.column.customer" />,
        cell: item => (
            <PaloNameListCell
                names={item.accountPurchases.map(purchase => purchase.customerAccount?.name)}
                companies={item.accountPurchases.map(purchase => purchase.customerAccount)}
                kind="customer"
            />
        ),
    },
    {
        key: 'responsibleContact',
        type: 'name',
        optional: true,
        sortKey: 'responsibleContact',
        head: <FormattedMessage id="app.common.responsibleContact" />,
        cell: item => (
            <ContactPresenter
                person={item.responsibleContact}
                fullName={item.responsibleContact?.fullName}
                isDeleted={item.responsibleContact?.isDeleted}
                isLocked={item.responsibleContact?.isLocked}
            />
        ),
    },
    {
        // Filled on 0,1 % of 33.208 inquiries, so it is off by default and
        // comes back through the column picker (LST-S8, GF 12.10.2026).
        key: 'rating',
        type: 'rating',
        hiddenByDefault: true,
        sortKey: 'rating',
        head: <FormattedMessage id="app.common.rating" />,
        cell: item =>
            item.rating ? (
                <Stack direction="row" gap={0.5} alignItems="center" justifyContent="flex-end">
                    <RatingIcon />
                    <Box>
                        <FormattedNumber value={item.rating} />
                    </Box>
                </Stack>
            ) : (
                '–'
            ),
    },
    {
        key: 'createdAt',
        type: 'date',
        // Room for the "21 days open" mark beside the date (#2260).
        width: '200px',
        sortKey: 'createdAt',
        head: <FormattedMessage id="app.table.column.createdAt" />,
        cell: item => (
            <Stack direction="row" alignItems="center" gap={1}>
                <FormattedDate value={item.createdAt} year="numeric" month="short" day="2-digit" />
                {item.isLongOpen && <PaloAgeBadge since={item.createdAt} warnFrom={0} messageId="app.age.openDays" />}
            </Stack>
        ),
    },
    {
        key: 'endDate',
        type: 'date',
        optional: true,
        sortKey: 'endDate',
        head: <FormattedMessage id="app.table.column.endDate" />,
        cell: item => item.endDate && <FormattedDate value={item.endDate} year="numeric" month="short" day="2-digit" />,
    },
];

export const PaloInquiryOverview = (): ReactElement => {
    const { hiddenColumnKeys, shownColumnKeys, toggleColumn, moveColumn, orderColumns, savedFiltersMenu } =
        usePaloListViews('inquiries');
    const columnFilter = usePaloColumnFilters('inquiries');
    const dispatch = useThunkDispatch();
    const navigate = useNavigate();
    const location = useLocation();
    const [searchParams, setSearchParams] = useSearchParams();
    const { sort, params } = usePaloListSort('inquiries');
    const intl = useIntl();
    const { flat: paginated, requesting, entity: inquiry } = useAppSelector(state => state.inquiries);

    const page = Number(searchParams.get('page') ?? 1); // 1-based
    const limit = Number(searchParams.get('limit') ?? 50);

    useEffect(() => {
        dispatch(InquiryActions.fetchInquiries(params));
    }, [dispatch, params]);

    const updateParams = (changes: Record<string, string>): void => {
        const next = new URLSearchParams(searchParams);
        Object.entries(changes).forEach(([key, value]) => next.set(key, value));
        setSearchParams(next);
    };

    const openDetail = (item: Inquiry): void => navigate(location.pathname + '/' + item.id);

    const headerActions = (
        <Button variant="contained" color="primary" onClick={() => navigate(location.pathname + '/new')}>
            <FormattedMessage id="app.inquiries.overview.new" />
        </Button>
    );

    const [isConverterVisible, setConverterVisible] = useState(false);

    const handleConvertOpen = (id: number): void => {
        dispatch(InquiryActions.resetInquiryState());
        dispatch(resetTransitions());
        dispatch(fetchTransitions(DocumentType.Inquiry, id));
        dispatch(InquiryActions.fetchInquiry(id));
        setConverterVisible(true);
    };

    const handleConvertClose = (): void => {
        dispatch(InquiryActions.requestEnd());
        dispatch(InquiryActions.resetInquiriesState());
        setConverterVisible(false);
    };

    const handleTransition = (transitionObject: Transition) =>
        dispatch(transitionInquiryIntoPurchaseOrders(transitionObject as OfferTransition));

    const rowActions = (item: Inquiry): PaloRowAction[] => [
        { label: 'Öffnen', onClick: () => openDetail(item) },
        { label: 'Umwandeln', onClick: () => handleConvertOpen(item.id) },
        {
            label: 'Duplizieren',
            onClick: () => dispatch(InquiryActions.duplicateInquiry(item.id, inquiryPartners(item))),
        },
        {
            label: 'Abschließen',
            onClick: () =>
                dispatch(InquiryActions.createOrUpdateInquiry({ ...item, status: DocumentStatus.Completed }, item.id)),
        },
        { label: 'Löschen', onClick: () => dispatch(InquiryActions.deleteInquiry(item.id)) },
    ];

    const handleExport = (): void => {
        const filename = `${intl.formatMessage({ id: 'app.export.inquiries' })}.csv`;
        dispatch(InquiryActions.exportInquiries(filename, searchParams));
    };

    return (
        <UrlFilter useLegacyFilter={true}>
            <PaloListView<Inquiry>
                title={<FormattedMessage id="app.navigation.inquiries" />}
                pageTitle={intl.formatMessage({ id: 'app.navigation.inquiries' })}
                onExport={handleExport}
                filters={
                    <>
                        <PaloInquiryOverviewFilter />
                        {savedFiltersMenu}
                    </>
                }
                filterChips={<PaloNamedFilterChips listKey="inquiries" />}
                sort={sort}
                onSortChange={(key, order) => updateParams({ sort: key, order, page: '1' })}
                searchValue={searchParams.get('search') ?? ''}
                searchPlaceholder="Nummer, Kunde, Lieferant, Artikel …"
                onSearchChange={value => {
                    const next = new URLSearchParams(searchParams);
                    if (value) {
                        next.set('search', value);
                    } else {
                        next.delete('search');
                    }
                    next.set('page', '1');
                    setSearchParams(next, { replace: true });
                }}
                headerActions={headerActions}
                columns={orderColumns(columns)}
                columnFilter={columnFilter}
                onMoveColumn={moveColumn}
                hiddenColumnKeys={hiddenColumnKeys}
                shownColumnKeys={shownColumnKeys}
                onToggleColumn={toggleColumn}
                items={paginated.items}
                getRowId={item => item.id}
                requesting={requesting}
                emptyLabel="Keine Anfragen."
                onRowClick={openDetail}
                rowActions={rowActions}
                pagination={{
                    count: paginated.total,
                    page,
                    limit,
                    onPageChange: newPage => updateParams({ page: String(newPage) }),
                    onLimitChange: newLimit => updateParams({ limit: String(newLimit), page: '1' }),
                }}
            >
                {inquiry && (
                    <ConverterReceiptModal
                        isVisible={isConverterVisible}
                        source={inquiry}
                        onClose={handleConvertClose}
                        onConvert={handleTransition}
                    />
                )}
            </PaloListView>
        </UrlFilter>
    );
};
